import React, { useRef, useEffect } from 'react'
import { ListFilter, ChevronDown } from 'lucide-react'

const OPTIONS = [10, 25, 50, 100]

interface RowsPerPageDropdownProps {
  value: number
  onChange: (value: number) => void
  direction?: 'up' | 'down'
}

export function RowsPerPageDropdown({
  value,
  onChange,
  direction = 'down',
}: Readonly<RowsPerPageDropdownProps>) {
  const [open, setOpen] = React.useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={ref} className='relative'>
      <button
        type='button'
        onClick={() => setOpen(!open)}
        className='flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 shadow-sm hover:border-primary/40 hover:shadow-md transition-all'
      >
        <ListFilter size={14} className='text-muted-foreground' />
        <span className='text-xs text-muted-foreground whitespace-nowrap'>
          Rows:
        </span>
        <span className='text-sm font-semibold text-foreground min-w-[2rem]'>
          {value}
        </span>
        <ChevronDown
          size={14}
          className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          className={`absolute left-0 min-w-[5rem] rounded-xl border border-slate-200 bg-white shadow-lg shadow-black/10 overflow-hidden z-50 ${
            direction === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          }`}
        >
          {OPTIONS.map(opt => (
            <button
              key={opt}
              type='button'
              onClick={() => {
                onChange(opt)
                setOpen(false)
              }}
              className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors ${
                value === opt
                  ? 'bg-primary/10 text-primary'
                  : 'text-foreground hover:bg-slate-50'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
